// app/javascript/controllers/autoscroll_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    force: Boolean,      // when true, always scroll (great for guest widget)
    threshold: Number    // px from bottom to still auto-scroll
  }

  connect() {
    // Default threshold: 80px
    if (this.thresholdValue == null) this.thresholdValue = 80

    // Bind to any images already present
    this.bindImages(this.element)

    // Scroll immediately on connect
    this.scrollToBottom({ instant: true })

    // Observe DOM changes (Turbo Streams append messages, images inserted, etc.)
    this.observer = new MutationObserver((mutations) => {
      let sawImage = false

      for (const m of mutations) {
        // If nodes were added, bind to images within them
        if (m.type === "childList") {
          m.addedNodes.forEach((node) => {
            // If the node is an <img> or contains images, bind them
            if (node.nodeType === 1) {
              if (node.matches?.('img[data-chat-image]')) {
                this.bindImage(node)
                sawImage = true
              }
              const imgs = node.querySelectorAll?.('img[data-chat-image]')
              imgs?.forEach((img) => {
                this.bindImage(img)
                sawImage = true
              })
            }
          })
        }
      }

      // Defer scroll for layout to settle
      this.deferScroll()

      // If we saw images, we'll also auto-scroll again when they finish loading via bindImage()
    })

    // Observe the full subtree so images added inside wrappers are detected
    this.observer.observe(this.element, { childList: true, subtree: true })

    // Keep bottom on window resize (optional quality-of-life)
    this._onResize = () => this.deferScroll()
    window.addEventListener("resize", this._onResize, { passive: true })
  }

  disconnect() {
    this.observer?.disconnect()
    window.removeEventListener("resize", this._onResize)
  }

  // Fired (via data-action) before Turbo renders a stream; we’ll scroll next frame
  turboStreamEnd() {
    this.deferScroll()
  }

  deferScroll() {
    cancelAnimationFrame(this._raf || 0)
    this._raf = requestAnimationFrame(() => this.scrollToBottom())
  }

  isNearBottom() {
    const el = this.element
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight
    return remaining <= this.thresholdValue
  }

  scrollToBottom({ instant = false } = {}) {
    const el = this.element
    const behavior = instant ? "auto" : "smooth"

    // For guest widget (force=true), always scroll.
    // For admin, only scroll if the user is already near the bottom.
    if (this.forceValue || this.isNearBottom()) {
      el.scrollTo({ top: el.scrollHeight, behavior })
    }
  }

  // ----- Image helpers -----

  bindImages(scopeEl) {
    const imgs = scopeEl.querySelectorAll('img[data-chat-image]')
    imgs.forEach((img) => this.bindImage(img))
  }

  bindImage(img) {
    // If already loaded, nudge scroll soon
    if (img.complete) {
      // A small timeout allows layout to finalize (dimensions applied)
      setTimeout(() => this.scrollToBottom(), 0)
      return
    }

    const onLoadOrError = () => {
      img.removeEventListener("load", onLoadOrError)
      img.removeEventListener("error", onLoadOrError)
      // Once the image resolves, scroll again
      this.scrollToBottom()
    }

    img.addEventListener("load", onLoadOrError, { once: true })
    img.addEventListener("error", onLoadOrError, { once: true })
  }
}
